import {
  AboveValueConfig,
  AfterDateValueConfigKind,
  BeforeDateValueConfigKind,
  BelowValueConfig,
  BetweenDateValueConfigKind,
  BetweenValueConfig,
  CohortFilterGranularity,
  ExactlyValueConfig,
  InTheLastValueConfigKind,
  UsersInteractionsFilterConfig,
  UserInteractionsValueConfigDates,
} from '@/api/generated/model';
import { FILTERS } from '@/models/common/filters';
import { CohortUserFilter } from './cohort-user-filters';

type InteractionCountConfig = AboveValueConfig | BelowValueConfig | BetweenValueConfig | ExactlyValueConfig;

const COHORT_PER_GRANULARITIES = new Set<string>([
  CohortFilterGranularity.day,
  CohortFilterGranularity.week,
  CohortFilterGranularity.month,
  CohortFilterGranularity.conversation,
]);

function normalizePerGranularityForApi(per: CohortFilterGranularity | undefined): CohortFilterGranularity | undefined {
  if (!per || !COHORT_PER_GRANULARITIES.has(per)) return undefined;
  return per;
}

export function normalizeCalendarWindowGranularity(granularity?: CohortFilterGranularity): CohortFilterGranularity {
  if (
    granularity === CohortFilterGranularity.day ||
    granularity === CohortFilterGranularity.week ||
    granularity === CohortFilterGranularity.month
  ) {
    return granularity;
  }
  return CohortFilterGranularity.week;
}

function getTimezoneOffset(): number {
  return new Date().getTimezoneOffset();
}

function normalizeInteractionCountConfigForApi(
  config: InteractionCountConfig,
  includePer: boolean,
): InteractionCountConfig {
  const offset = typeof config.offset === 'number' ? config.offset : getTimezoneOffset();
  const per = normalizePerGranularityForApi(includePer && 'per' in config ? config.per : undefined);

  if (config.kind === 'between' && Array.isArray(config.value)) {
    const normalized = {
      kind: 'between' as const,
      value: config.value as [number, number],
      offset,
    };
    return per ? { ...normalized, per } : normalized;
  }

  if (config.kind === 'below') {
    const normalized = { kind: 'below' as const, value: config.value, offset };
    return per ? { ...normalized, per } : normalized;
  }

  if (config.kind === 'exactly') {
    const normalized = { kind: 'exactly' as const, value: config.value, offset };
    return per ? { ...normalized, per } : normalized;
  }

  const normalized = { kind: 'above' as const, value: config.value as number, offset };
  return per ? { ...normalized, per } : normalized;
}

function normalizeOptionalDatesForApi(
  dates: UserInteractionsValueConfigDates | undefined | null,
): UserInteractionsValueConfigDates | undefined {
  if (!dates || typeof dates !== 'object' || !('kind' in dates)) return undefined;

  if (dates.kind === InTheLastValueConfigKind.in_the_last) {
    return {
      kind: InTheLastValueConfigKind.in_the_last,
      value: dates.value,
      granularity: normalizeCalendarWindowGranularity(dates.granularity),
      ...(dates.additional_filter ? { additional_filter: dates.additional_filter } : {}),
      ...(typeof dates.offset === 'number' ? { offset: dates.offset } : {}),
    };
  }

  if (
    dates.kind === AfterDateValueConfigKind.after ||
    dates.kind === BeforeDateValueConfigKind.before ||
    dates.kind === BetweenDateValueConfigKind.between
  ) {
    return dates;
  }

  return undefined;
}

function normalizeUsersInteractionsValuesForApi(
  values: UsersInteractionsFilterConfig['values'],
): UsersInteractionsFilterConfig['values'] {
  const interactions_count = normalizeInteractionCountConfigForApi(
    values.interactions_count as InteractionCountConfig,
    true,
  );

  const dates = normalizeOptionalDatesForApi(values.dates);

  const result: UsersInteractionsFilterConfig['values'] = {
    interactions_count,
    ...(dates ? { dates } : {}),
  };

  return result as UsersInteractionsFilterConfig['values'];
}

export function normalizeCohortUserFilterForApi(filter: CohortUserFilter): CohortUserFilter {
  switch (filter.column) {
    case FILTERS.usersInteraction:
      return {
        ...filter,
        values: normalizeUsersInteractionsValuesForApi(filter.values),
      };

    case FILTERS.userConversationsCount: {
      const dates = normalizeOptionalDatesForApi(filter.values.dates);
      const values = {
        conversations_count: normalizeInteractionCountConfigForApi(
          filter.values.conversations_count as InteractionCountConfig,
          false,
        ),
        ...(dates ? { dates } : {}),
      };
      return { ...filter, values };
    }

    case FILTERS.userConversationLength: {
      const dates = normalizeOptionalDatesForApi(filter.values.dates);
      const values = {
        interactions_count: normalizeInteractionCountConfigForApi(
          filter.values.interactions_count as InteractionCountConfig,
          false,
        ),
        ...(dates ? { dates } : {}),
      };
      return { ...filter, values };
    }

    case FILTERS.activePeriods:
      return {
        ...filter,
        values: {
          ...filter.values,
          active_count: normalizeInteractionCountConfigForApi(
            filter.values.active_count as InteractionCountConfig,
            false,
          ),
          granularity: normalizeCalendarWindowGranularity(filter.values.granularity),
        },
      };

    case FILTERS.avgSessionLength: {
      const dates = normalizeOptionalDatesForApi(filter.values.dates);
      const values = {
        duration: normalizeInteractionCountConfigForApi(filter.values.duration as InteractionCountConfig, false),
        ...(dates ? { dates } : {}),
      };
      return { ...filter, values };
    }

    case FILTERS.firstInteraction:
    case FILTERS.lastInteraction:
    case FILTERS.lastInteractionTag:
      return filter;

    default: {
      const exhaustive: never = filter;
      return exhaustive;
    }
  }
}
